/**
 * Connections Page
 *
 * Main page for managing OAuth connections to external platforms
 * Uses always-visible Platform Slots for Square, QuickBooks, and Shopify
 *
 * Features:
 * - Platform slots (always visible, one per platform)
 * - Visual progress indicator (X of 3 connected)
 * - Environment toggle (production/sandbox) for rp_admin/rp_support
 * - Post-connection guidance toast
 * - Action handling (pause/unpause/archive/restore/delete/reconnect)
 *
 * Based on Inara's approved redesign plan.
 */

import { useState, useEffect } from 'react'
import { Row, Col, Typography, Button, Space, Spin, Alert, Radio, Progress, message } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import {
  useConnections,
  useDeleteConnection,
  useReconnectConnection,
  useTestConnection,
  usePauseConnection,
  useUnpauseConnection,
  useRestoreConnection,
} from '@/hooks'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import {
  PlatformSlot,
  TestResultModal,
} from '@/components/connections'
import { NoOrgSelectedPrompt } from '@/components/NoOrgSelectedPrompt'
import { generatePKCEPair } from '@/utils/pkce'
import { getAllPlatformConfigs } from '@/utils/platformConfig'
import { canSeeSandbox } from '@/utils/roleUtils'
import { computeDisplayState } from '@/utils/connectionState'
import type { Connection, ConnectionAction, TestConnectionResult, Platform } from '@/types/connection.types'

const { Title, Text } = Typography

/**
 * Connections Page
 *
 * Main interface for managing platform connections.
 * Displays always-visible slots for Square, QuickBooks, and Shopify.
 *
 * @example
 * ```tsx
 * <Route path="/connections" element={<ConnectionsPage />} />
 * ```
 */
export const ConnectionsPage: React.FC = () => {
  const [environment, setEnvironment] = useState<'production' | 'sandbox'>('production')
  const [showTestModal, setShowTestModal] = useState(false)
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null)
  const [testingPlatform, setTestingPlatform] = useState<Platform | null>(null)

  const navigate = useNavigate()

  // Get current user to check org context and role permissions
  const { data: user, isLoading: isUserLoading } = useCurrentUser()
  const hasOrgContext = !!user?.currentOrganization
  const showEnvironmentPicker = canSeeSandbox(user)

  // Fetch connections - disabled when no org is selected to prevent RLS errors
  const { data: connections, isLoading, error, refetch } = useConnections({ enabled: hasOrgContext })

  // Get all platform configs
  const platformConfigs = getAllPlatformConfigs()

  // Calculate connected count for CURRENT environment only
  // Must match what getConnectionForPlatform returns (filters by prod/sandbox)
  const connectedCount = platformConfigs.filter((config) => {
    const targetPlatform = environment === 'sandbox' ? config.sandboxPlatform : config.platform
    const connection = connections?.find((c) => c.platform === targetPlatform && !c.deletedAt)
    if (!connection) return false
    const displayState = computeDisplayState(connection)
    return displayState.label === 'Connected'
  }).length

  // Handle OAuth callback notifications (success or cancellation)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const oauthSuccess = urlParams.get('oauth_success')
    const cancelled = urlParams.get('cancelled')

    if (oauthSuccess === 'true') {
      message.success({
        content: (
          <span>
            Connection established successfully!{' '}
            <a onClick={() => navigate('/tools/vendor-import')}>Upload vendor prices</a>
          </span>
        ),
        duration: 10,
      })
    } else if (cancelled === 'true') {
      message.info({
        content: 'Connection cancelled. You can try again anytime.',
        duration: 5,
      })
    }

    // Clean up URL params
    if (oauthSuccess || cancelled) {
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  // Mutations for connection actions (simplified)
  const deleteConnection = useDeleteConnection()
  const reconnectConnection = useReconnectConnection()
  const testConnection = useTestConnection()
  const pauseConnection = usePauseConnection()
  const unpauseConnection = useUnpauseConnection()
  const restoreConnection = useRestoreConnection()

  // Check if any mutation is pending
  const isActionPending =
    deleteConnection.isPending ||
    reconnectConnection.isPending ||
    testConnection.isPending ||
    pauseConnection.isPending ||
    unpauseConnection.isPending ||
    restoreConnection.isPending

  // Handle connection actions (simplified)
  const handleAction = async (action: ConnectionAction, connection: Connection) => {
    try {
      switch (action) {
        case 'delete':
          // "Disconnect" performs soft delete
          await deleteConnection.mutateAsync(connection.uid)
          break

        case 'reconnect':
          // Generate PKCE pair (RFC 7636 compliant)
          const { codeVerifier, codeChallenge } = await generatePKCEPair()

          // Initiate OAuth flow for reconnection with PKCE
          const result = await reconnectConnection.mutateAsync({
            uid: connection.uid,
            request: {
              redirectUri: `${window.location.origin}/oauth/callback`,
              codeChallenge: codeChallenge,
            },
          })

          // Store PKCE verifier and CSRF token for callback validation
          sessionStorage.setItem('oauth_code_verifier', codeVerifier)
          sessionStorage.setItem('oauth_state', result.csrfToken)
          sessionStorage.setItem('oauth_connection_uid', connection.uid)

          // Redirect to platform OAuth page
          window.location.href = result.authUrl
          break

        case 'test': {
          // Show loading modal
          setTestingPlatform(connection.platform)
          setTestResult(null)
          setShowTestModal(true)

          // Run test - hook now returns TestConnectionResult directly
          const testResultData = await testConnection.mutateAsync(connection.uid)
          setTestResult(testResultData)
          break
        }

        case 'pause':
          await pauseConnection.mutateAsync(connection.uid)
          break

        case 'unpause':
          await unpauseConnection.mutateAsync(connection.uid)
          break

        case 'restore':
          await restoreConnection.mutateAsync(connection.uid)
          break

        default:
          console.warn('Unknown action:', action)
      }
    } catch (error) {
      // Error already shown via mutation onError
      console.error('Action failed:', action, error)

      // Close test modal on error
      if (action === 'test') {
        setShowTestModal(false)
        setTestResult(null)
        setTestingPlatform(null)
      }
    }
  }

  // Helper: Get connection for a platform+environment
  // Returns the NEWEST connection (by createdAt) to handle legacy duplicates gracefully
  const getConnectionForPlatform = (platformName: string): Connection | null => {
    if (!connections) return null

    const platformConfig = platformConfigs.find((p) => p.name === platformName)
    if (!platformConfig) return null

    const targetPlatform: Platform =
      environment === 'sandbox' ? platformConfig.sandboxPlatform : platformConfig.platform

    // Sort by createdAt DESC to get newest first, then take first match
    const matches = connections
      .filter((c) => c.platform === targetPlatform && !c.deletedAt)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    return matches[0] || null
  }

  // Loading user state
  if (isUserLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
        }}
      >
        <Space direction="vertical" align="center" size="large">
          <Spin size="large" />
          <Text type="secondary">Loading...</Text>
        </Space>
      </div>
    )
  }

  // No organization selected - show friendly prompt instead of RLS errors
  if (!hasOrgContext) {
    return <NoOrgSelectedPrompt featureName="connections" />
  }

  // Loading connections state
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
        }}
      >
        <Space direction="vertical" align="center" size="large">
          <Spin size="large" />
          <Text type="secondary">Loading connections...</Text>
        </Space>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
        <Alert
          message="Failed to Load Connections"
          description={error.message}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      </div>
    )
  }

  // Main view - Platform Slots (always visible)
  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px',
        }}
      >
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Connections
          </Title>
          <Text type="secondary">Connect to your platforms to sync pricing data</Text>
        </div>

        <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
          Refresh
        </Button>
      </div>

      {/* Progress Indicator */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '24px',
          padding: '16px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
        }}
      >
        <Progress
          type="circle"
          percent={(connectedCount / 3) * 100}
          format={() => `${connectedCount}/3`}
          width={60}
          strokeColor={connectedCount === 3 ? '#52c41a' : '#1890ff'}
        />
        <div>
          <Text strong style={{ display: 'block', fontSize: '16px' }}>
            {connectedCount === 0 && 'Connect your first platform to get started'}
            {connectedCount === 1 && 'Great start! Connect more platforms to expand your reach'}
            {connectedCount === 2 && 'Almost there! One more platform to connect'}
            {connectedCount === 3 && 'All platforms connected!'}
          </Text>
          <Text type="secondary" style={{ fontSize: '13px' }}>
            Connect Square, QuickBooks, and Shopify to manage pricing across all your sales channels
          </Text>
        </div>
      </div>

      {/* Environment Toggle (only for rp_admin/rp_support) */}
      {showEnvironmentPicker && (
        <div style={{ marginBottom: '24px' }}>
          <Text strong style={{ marginRight: '12px' }}>
            Environment:
          </Text>
          <Radio.Group value={environment} onChange={(e) => setEnvironment(e.target.value)}>
            <Radio.Button value="production">Production</Radio.Button>
            <Radio.Button value="sandbox">Sandbox</Radio.Button>
          </Radio.Group>
        </div>
      )}

      {/* Platform Slots Grid */}
      <Row gutter={[16, 16]}>
        {platformConfigs.map((platformConfig) => {
          const connection = getConnectionForPlatform(platformConfig.name)

          return (
            <Col xs={24} sm={12} md={8} key={platformConfig.name}>
              <PlatformSlot
                platformConfig={platformConfig}
                environment={environment}
                connection={connection}
                onAction={handleAction}
                loading={isActionPending}
              />
            </Col>
          )
        })}
      </Row>

      {/* Test Result Modal */}
      <TestResultModal
        open={showTestModal}
        onClose={() => {
          setShowTestModal(false)
          setTestResult(null)
          setTestingPlatform(null)
        }}
        testResult={testResult}
        platform={testingPlatform ?? 'square'}
        isLoading={testConnection.isPending && !testResult}
      />
    </div>
  )
}

export default ConnectionsPage
